import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [form, setForm] = useState({ video_url: "", poster_url: "", autoplay: false });
  const [passcode, setPasscode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/video`).then((r) => {
      setForm({
        video_url: r.data.video_url || "",
        poster_url: r.data.poster_url || "",
        autoplay: Boolean(r.data.autoplay),
      });
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings/video`, { ...form, passcode });
      toast.success("Video settings saved — the landing page is updated.");
    } catch (err) {
      if (err.response?.status === 403) toast.error("Wrong passcode — settings not saved.");
      else toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition";

  return (
    <div className="grain min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 sm:pt-32 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-sm font-semibold text-walnutsoft hover:text-terracotta transition-colors mb-8">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-walnut mb-2">Landing video</h1>
          <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-8">
            Update the "See Damali in action" video on the homepage. Paste a YouTube link
            (recommended — fastest loading) or a direct .mp4 link, then save. No code
            changes needed.
          </p>

          <form data-testid="admin-video-form" onSubmit={save} className="bg-white/70 border border-sand rounded-3xl p-7 flex flex-col gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-walnutsoft mb-1.5">Video link</label>
              <input
                data-testid="admin-video-url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=… or https://…/video.mp4"
                className={input}
              />
              <p className="text-xs text-walnutmuted mt-1.5">Leave empty to show the "coming soon" placeholder.</p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-walnutsoft mb-1.5">Poster image link (optional)</label>
              <input
                data-testid="admin-poster-url"
                value={form.poster_url}
                onChange={(e) => setForm({ ...form, poster_url: e.target.value })}
                placeholder="https://…/cover.jpg"
                className={input}
              />
            </div>
            <label className="flex items-center gap-3 text-sm text-walnut cursor-pointer">
              <input
                data-testid="admin-autoplay-toggle"
                type="checkbox"
                checked={form.autoplay}
                onChange={(e) => setForm({ ...form, autoplay: e.target.checked })}
                className="w-4 h-4 accent-[#4FA3E3]"
              />
              Autoplay the video (plays muted, as browsers require)
            </label>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-walnutsoft mb-1.5">Admin passcode</label>
              <input
                data-testid="admin-passcode-input"
                type="password"
                required
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Your admin passcode"
                className={input}
              />
            </div>
            <button
              data-testid="admin-save-button"
              type="submit"
              disabled={saving}
              className="mt-2 rounded-full bg-terracotta text-cream py-3.5 font-display font-bold text-sm flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors disabled:opacity-60"
            >
              <Save size={16} /> {saving ? "Saving…" : "Save video settings"}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
